// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { InstitutionCatalogOption } from '@client/banks/utils/institution-catalog';
import {
  Alert,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { AssetLogo } from '@shared/ui-kit/components/asset-logo';
import { Input } from '@shared/ui-kit/components/input';
import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils';
import styles from '../styles/institution-catalog.module.css';

export interface InstitutionCatalogPickerProps {
  t: (key: string, options?: { ns: string }) => string;
  countryOptions: { value: string; label: string }[];
  country: string;
  search: string;
  institutions: InstitutionCatalogOption[];
  countryByCode: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
  hasInstitutions: boolean;
  onRetry: () => void;
  onCountryChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onSelectInstitution: (listKey: string) => void;
}

export const InstitutionCatalogPicker = ({
  t,
  countryOptions,
  country,
  search,
  institutions,
  countryByCode,
  isLoading,
  isError,
  hasInstitutions,
  onRetry,
  onCountryChange,
  onSearchChange,
  onSelectInstitution,
}: InstitutionCatalogPickerProps) => (
  <div className={styles.catalog}>
    <div className={styles.filters}>
      <div className={styles['country-selector-wrapper']}>
        <OptionsSelector
          optionsAvailable={countryOptions}
          onChangeValueSelected={onCountryChange}
          optionSelected={country}
          defaultText={t('aggregator.all-countries', { ns: 'aggregator' })}
          selectType="white"
          searchBar
          selectedAll={false}
          data-testid="bank-upload-institution-country-selector"
        />
      </div>

      <div className={styles['search-wrapper']}>
        <AppIcon icon="Search" color="disabled" size="base" />
        <Input
          value={search}
          onChange={event => onSearchChange(event.target.value)}
          placeholder={t('aggregator.search-bank-placeholder', {
            ns: 'aggregator',
          })}
          className={styles.search}
          data-testid="bank-upload-institution-search"
        />
      </div>
    </div>

    {isLoading ? (
      <div className={styles.loading} data-testid="bank-upload-institution-loading">
        <LinearProgress classes={{ root: 'w-full rounded-lg' }} />
      </div>
    ) : null}

    {isError ? (
      <Alert
        severity="warning"
        className={styles['error-alert']}
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            <Translation i18nKey="aggregator.refresh" ns="aggregator" />
          </Button>
        }
      >
        <Translation i18nKey="aggregator.bank-search-error" ns="aggregator" />
      </Alert>
    ) : null}

    {!isLoading && !isError && !hasInstitutions ? (
      <Alert severity="info">
        <Translation i18nKey="aggregator.no-bank-results" ns="aggregator" />
      </Alert>
    ) : null}

    {!isLoading && !isError && hasInstitutions ? (
      <div className={styles.list}>
        {institutions.map(institution => (
          <button
            type="button"
            key={institution.listKey}
            onClick={() => onSelectInstitution(institution.listKey)}
            className={classNames(styles.card, {
              [styles['card-selected']]: institution.isSelected,
            })}
            data-testid={`bank-upload-institution-option-${institution.id}`}
          >
            <div className={styles['card-logo']}>
              {institution.logo_url ? (
                <AssetLogo
                  logoUrl={institution.logo_url}
                  className={styles['card-logo-image']}
                />
              ) : (
                <AppIcon icon="Building" color="disabled" size="base" />
              )}
            </div>

            <div className={styles['card-content']}>
              <div className={styles['card-title-row']}>
                <Typography className={styles['card-title']}>
                  {institution.name}
                </Typography>

                {institution.is_oneshot ? (
                  <Tooltip
                    title={
                      <Translation
                        i18nKey="aggregator.one-shot-message"
                        ns="aggregator"
                      />
                    }
                  >
                    <Chip
                      size="small"
                      icon={
                        <AppIcon
                          icon="RefreshTwoArrows"
                          size="base"
                          color="inherit"
                        />
                      }
                      label={
                        <Translation
                          i18nKey="aggregator.one-shot"
                          ns="aggregator"
                        />
                      }
                      className={styles['one-shot-chip']}
                    />
                  </Tooltip>
                ) : null}

                {institution.is_business ? (
                  <Chip
                    size="small"
                    label={
                      <Translation i18nKey="aggregator.business" ns="aggregator" />
                    }
                    className={styles['business-chip']}
                  />
                ) : null}
              </div>

              <Typography className={styles['card-country']}>
                {countryByCode.get(institution.country) ?? institution.country}
              </Typography>
            </div>

            <div className={styles['card-action']}>
              {institution.isSelected ? (
                <AppIcon icon="CheckCircle" color="inherit" size="lg" />
              ) : null}
            </div>
          </button>
        ))}
      </div>
    ) : null}
  </div>
);
